import { useMemo } from 'react';
import {
    EnrollmentsOnlyQuery,
    EnrollmentStatus,
    useEnrollmentsOnlyQuery,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import { useUserSession } from './user/useDefaultSession';

export const EnrollmentQueryKeys = {
    key: 'enrollmentsOnly',
    enrollmentByUser: (
        userId: string,
        includeChildren: boolean = false,
        includePending: boolean = false,
        includePast: boolean = false,
    ) => ({
        userId,
        includeChildren,
        includePending,
        includePast,
    }),
};

export function useEnrollHandler() {
    const [user] = useUserSession();
    const { data, isLoading, isRefetching } = useEnrollmentsOnlyQuery(
        getClient('courses'),
        EnrollmentQueryKeys.enrollmentByUser(user.id, false, true, false),
        {
            notifyOnChangeProps: ['data', 'isLoading', 'isRefetching'],
            staleTime: 1000 * 60 * 10, // 10 mins
        },
    );

    const filteredEnrolls = useMemo(() => {
        const leadingCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const takingCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const takingCoursesIds: string[] = [];
        const observingCouses: EnrollmentsOnlyQuery['enrollments'] = [];
        const acceptCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const duplicates = new Set<string>();

        data?.enrollments?.forEach((e) => {
            const hasKey = duplicates.has(duplicateKey(e));
            if (e?.status === EnrollmentStatus.PendingAccept) {
                acceptCourses.push(e);
            } else if (e?.status === EnrollmentStatus.PendingConfirm) {
            } else if (e?.role?.type === 'leader' && !hasKey) {
                leadingCourses.push(e);
            } else if (e?.role?.type === 'parent' && !hasKey) {
                observingCouses.push(e);
            } else {
                if (!hasKey) {
                    takingCourses.push(e);
                    takingCoursesIds.push(e?.course?.id);
                }
            }
            duplicates.add(duplicateKey(e));
        });
        return { leadingCourses, takingCourses, observingCouses, acceptCourses, takingCoursesIds };
    }, [data]);
    return { data, enrolls: filteredEnrolls, loading: isLoading, isRefetching };
}

export function useAllMyEnrollmentsHandler() {
    const [user] = useUserSession();
    const { data, isLoading, isRefetching } = useEnrollmentsOnlyQuery(
        getClient('courses'),
        EnrollmentQueryKeys.enrollmentByUser(user.id, false, true, true),
        {
            notifyOnChangeProps: ['data', 'isLoading', 'isRefetching'],
            staleTime: 1000 * 60 * 10, // 10 mins
        },
    );

    const filteredEnrolls = useMemo(() => {
        const leadingCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const takingCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const takingCoursesIds: string[] = [];
        const observingCouses: EnrollmentsOnlyQuery['enrollments'] = [];
        const acceptCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const pendingConfirmCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const concludedCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const droppedCourses: EnrollmentsOnlyQuery['enrollments'] = [];
        const duplicates = new Set<string>();

        data?.enrollments?.forEach((e) => {
            const hasKey = duplicates.has(duplicateKey(e));
            if (e?.status === EnrollmentStatus.PendingAccept) {
                acceptCourses.push(e);
            } else if (e?.status === EnrollmentStatus.PendingConfirm) {
                pendingConfirmCourses.push(e);
            } else if (e?.status === EnrollmentStatus.Concluded) {
                concludedCourses.push(e);
            } else if (e?.status === EnrollmentStatus.Dropped) {
                droppedCourses.push(e);
            } else if (e?.role?.type === 'leader' && !hasKey) {
                leadingCourses.push(e);
            } else if (e?.role?.type === 'parent' && !hasKey) {
                observingCouses.push(e);
            } else {
                if (!hasKey) {
                    takingCourses.push(e);
                    takingCoursesIds.push(e?.course?.id);
                }
            }
            duplicates.add(duplicateKey(e));
        });
        return { leadingCourses, takingCourses, concludedCourses, droppedCourses, observingCouses, acceptCourses, takingCoursesIds, pendingConfirmCourses };
    }, [data]);
    return { data, enrolls: filteredEnrolls, loading: isLoading, isRefetching };
}

function duplicateKey(e: NonNullable<EnrollmentsOnlyQuery['enrollments']>[0]) {
    return `${e.role.type}${e.course.id}`;
}
